import Action from 'actions';
import BaseLP from 'services/sports/default/base-lp';
import Const from 'constants';
import {addRoleSuffix, getSimplexUnitName} from 'helpers/simplex-helper';
import {getSimplexService} from 'services/sports/factory';

export default class LP extends BaseLP {
    preparePlayerByLPPlayerId({incomingValues: {id}}) {
        const state = Action.getState();
        const {preparedPlayers} = state.playersData;
        const playersGroupedById = preparedPlayers.groupedBy.playerId();

        let unit = id.replace(Const.LP_UNIT_PREFIX, '').split(Const.LP_ITEM_DELIMITER);

        let player = {...playersGroupedById[unit[0]]};

        if (Const.LP_CAPTAIN_PLAYER_POSITION == unit[1]) {
            player.setAsCaptain();
        }

        player.position = Number(player.position[0]);

        return player;
    }

    prepareMaxSection({incomingValues = {}}) {
        return this.exec('prepareMaxSection_ShowDown', incomingValues);
    }

    prepareSectionByTeam({incomingValues = {}}) {
        return this.exec('prepareSectionByTeam_ShowDown', incomingValues);
    }

    prepareSectionByUniquePosition({incomingValues = {}}) {
        return this.exec('prepareSectionByUniquePosition_ShowDown', incomingValues);
    }

    prepareSectionByCaptainPosition({incomingValues = {}}) {
        return this.exec('prepareSectionByCaptainPosition_ShowDown', incomingValues);
    }

    prepareSectionBySalary({incomingValues = {}}) {
        return this.exec('prepareSectionBySalaryRoles', {
            salaryPropsByRoles: {
                [Const.CAPTAIN_MODE]: 'salary',
                [Const.FLEX_MODE]: 'salary',
            },
        });
    }

    prepareSectionByPlayer({incomingValues = {}}) {
        return this.exec('prepareSectionByPlayer_ShowDown', incomingValues);
    }

    prepareSectionByCreatedLineups({incomingValues = {}}) {
        return this.exec('prepareSectionByCreatedLineups_ShowDown', incomingValues);
    }

    prepareSectionByExposureMax({incomingValues = {}}) {
        return this.exec('prepareSectionByExposureMax_ShowDown', incomingValues, {});
    }

    prepareSectionByExposureMin({incomingValues = {}}) {
        return this.exec('prepareSectionByExposureMin_ShowDown', incomingValues);
    }

    prepareSectionByStacks({incomingValues = {}}) {
        return this.exec('prepareSectionByStacks_ShowDown', incomingValues);
    }

    prepareGeneralSection({incomingValues = {}}) {
        return this.exec('prepareGeneralSection_ShowDown', incomingValues);
    }

    prepareBoundSection({incomingValues = {}}) {
        const state = Action.getState();
        const {preparedPlayers} = state.playersData;
        const playersGroupedById = preparedPlayers.groupedBy.playerId();

        let result = {};
        Object.keys(playersGroupedById).map((pId) => {
            let player = playersGroupedById[pId];
            let prefix = getSimplexUnitName(pId);

            const isExcludedByCapMode = player.isExcludedByMode(Const.CAPTAIN_MODE, {
                salaryMode: Const.FLEX_MODE,
            });
            result[addRoleSuffix({prefix, role: Const.CAPTAIN_MODE})] = {
                atLeast: 0,
                atMost: isExcludedByCapMode ? 0 : 1,
            };

            const isExcludedByFlexMode = player.isExcludedByMode(Const.FLEX_MODE);
            result[addRoleSuffix({prefix, role: Const.FLEX_MODE})] = {
                atLeast: 0,
                atMost: isExcludedByFlexMode ? 0 : 1,
            };
        });
        // console.log("bound\n",result);
        return result;
    }

    prepareMaxRandomSection({incomingValues = {}}) {
        return this.exec('prepareMaxRandomSection_ShowDown', incomingValues);
    }

    prepareSectionByPosition({incomingValues = {}}) {
        const state = Action.getState();
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = state;
        const players = preparedPlayers.groupedBy.allowedPositions();
        const positions = sportConfig.getConfigProp('positions', []);

        let result = '';

        Object.keys(positions).map((pos) => {
            let playersOnPos = '',
                posObj = positions[pos];

            players.map((player) => {
                if (!inArray(player.position, posObj.allowedPositions)) {
                    return;
                }

                const lpPlayerId = addRoleSuffix({
                    prefix: player.getId(),
                    role: Const.DEFAULT_MODE,
                });
                playersOnPos += '+' + getSimplexUnitName(lpPlayerId);
            });

            if (!isEmpty(playersOnPos)) {
                result += playersOnPos + '=' + posObj.maxPlayers + '\n';
            }
        });
        // console.log("position\n",result);
        return result;
    }

    minTeams({incomingValues = {}}) {
        const state = Action.getState();

        const constraints = getSimplexService(state.sportConfig, {store: this.store}).exec(
            'minTeams',
            incomingValues,
            [],
        );

        let result = '';

        if (!isEmpty(constraints)) {
            result = this.exec(
                'convertConstraintsToStr',
                {
                    constraints,
                },
                '',
            );
        }
        // console.log("minTeams\n", result);
        return result;
    }

    subjectToPermanent() {
        const modes = [Const.DEFAULT_MODE, Const.CAPTAIN_MODE];
        const constraints = {
            players: this.exec('prepareSectionByPlayer', {}, ''),
            salary: this.exec('prepareSectionBySalary', {}, ''),
            positions: this.exec('prepareSectionByPosition', {}, ''),
            teams: this.exec('prepareSectionByTeam', {}, ''),
            minTeams: this.exec(
                'minTeams',
                {
                    modes,
                },
                '',
            ),
            maxSingleGamePlayersByModes: this.exec(
                'maxSingleGamePlayersByModes',
                {
                    modes,
                },
                '',
            ),
            opposingTeamsInLineup: this.exec(
                'opposingTeamsInLineup',
                {
                    modes,
                },
                '',
            ),
            captainPositions: this.exec('prepareSectionByCaptainPosition', {}, ''),
        };
        return {...constraints};
    }

    subjectToAlterable() {
        const constraints = {
            uniquePositions: this.exec('prepareSectionByUniquePosition', {}, ''),
            createdLineups: this.exec('prepareSectionByCreatedLineups', {}, ''),
            exposureMax: this.exec('prepareSectionByExposureMax', {}, ''),
            exposureMin: this.exec('prepareSectionByExposureMin', {}, ''),
            randomTeamStack: this.exec(
                'stackRandomTeam',
                {
                    modes: [Const.DEFAULT_MODE, Const.CAPTAIN_MODE],
                },
                '',
            ),
            playerCombinationStacking: this.exec(
                'playerCombinationStacking',
                {
                    modes: [Const.DEFAULT_MODE, Const.CAPTAIN_MODE],
                },
                '',
            ),
        };
        return {...constraints};
    }
}
